import React, { useState, useEffect } from 'react';
import './trustmark.css';
import { Shield, Heart, Zap, Lock, ShieldCheck } from 'lucide-react';
import Safe from './safer.png';
import Secure from './secure.png';
import Trafic from './trafic.png';
import demoGif from './demo.gif';

const Trustmark = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const benefits = [
    {
      icon: Shield,
      title: "Enhanced Experience",
      description: "Seamless authentication without compromising security. Your data, protected effortlessly.",
    },
    {
      icon: Lock,
      title: "Privacy Focused",
      description: "Protection that respects privacy. No personal data collection, complete peace of mind.",
    },
    {
      icon: Zap,
      title: "Advanced Security",
      description: "Real-time threat detection integrated with your existing systems. Always a step ahead.",
    },
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setActiveIndex((current) => (current + 1) % benefits.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const handleDotClick = (index) => {
    setActiveIndex(index);
    setIsAutoPlaying(false);
  };

  const scrollToSection = () => {
    const element = document.getElementById('target-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToPricing = () => {
    window.location.href = '/TrustMark-pricing';
  };

  return (
    <div className="trustmark-container">
      {/* Hero Section */}
      <section className="trustmark-hero">
        <div className="trustmark-hero-content">
          <h1 className="trustmark-hero-title">
            Erasys <span className="trustmark-hero-accent">TrustMark</span>
          </h1>
          <p className="trustmark-hero-subtitle">
            Revolutionize online access control by verifying genuine users and protecting your site from malicious automated threats.
          </p>
          <div className="trustmark-hero-buttons">
            <button className="trustmark-btn trustmark-btn-primary" onClick={scrollToSection}>
              Learn More
            </button>
            <button className="trustmark-btn trustmark-btn-secondary" onClick={goToPricing}>
              Get Started
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="trustmark-benefits">
        <div className="trustmark-section-wrapper">
          <div className="trustmark-section-header">
            <h2 className="trustmark-section-title">Security that puts you first.</h2>
            <p className="trustmark-section-subtitle">
              Advanced protection designed around your needs.
            </p>
          </div>

          <div className="trustmark-carousel">
            <div
              className="trustmark-carousel-slider"
              style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            >
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div key={index} className="trustmark-carousel-slide">
                    <div className="trustmark-benefit-card">
                      <div className="trustmark-benefit-icon">
                        <IconComponent strokeWidth={1.5} />
                      </div>
                      <h3 className="trustmark-benefit-title">{benefit.title}</h3>
                      <p className="trustmark-benefit-description">{benefit.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="trustmark-carousel-dots">
              {benefits.map((_, index) => (
                <button
                  key={index}
                  className={`trustmark-carousel-dot ${index === activeIndex ? 'active' : ''}`}
                  onClick={() => handleDotClick(index)}
                  aria-label={`Slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="trustmark-features">
        <div className="trustmark-section-wrapper">
          <div className="trustmark-section-header">
            <h2 className="trustmark-section-title">Security that's built around you.</h2>
            <p className="trustmark-section-subtitle">
              Intelligent protection that adapts to your needs.
            </p>
          </div>
          
          <div className="trustmark-features-grid">
            <div className="trustmark-feature-row">
              <div className="trustmark-feature-content">
                <div className="trustmark-feature-icon">
                  <Shield strokeWidth={1.5} />
                </div>
                <h3 className="trustmark-feature-title">Advanced Protection</h3>
                <p className="trustmark-feature-description">
                  Real-time threat detection powered by machine learning keeps you safe from emerging threats.
                </p>
              </div>
              <div className="trustmark-feature-image">
                <img src={Safe} alt="Advanced Protection" />
              </div>
            </div>

            <div className="trustmark-feature-row trustmark-feature-row-reverse">
              <div className="trustmark-feature-content">
                <div className="trustmark-feature-icon">
                  <Zap strokeWidth={1.5} />
                </div>
                <h3 className="trustmark-feature-title">Lightning Fast</h3>
                <p className="trustmark-feature-description">
                  Optimized performance ensures your protection never slows you down.
                </p>
              </div>
              <div className="trustmark-feature-image">
                <img src={Secure} alt="Lightning Fast" />
              </div>
            </div>

            <div className="trustmark-feature-row">
              <div className="trustmark-feature-content">
                <div className="trustmark-feature-icon">
                  <Lock strokeWidth={1.5} />
                </div>
                <h3 className="trustmark-feature-title">Privacy First</h3>
                <p className="trustmark-feature-description">
                  Your data stays private with end-to-end encryption and zero-knowledge architecture.
                </p>
              </div>
              <div className="trustmark-feature-image">
                <img src={Trafic} alt="Privacy First" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="trustmark-capabilities">
        <div className="trustmark-section-wrapper">
          <div className="trustmark-section-header">
            <h2 className="trustmark-section-title">
              Powerful security.<br />
              Effortless integration.
            </h2>
            <p className="trustmark-section-subtitle">
              Advanced protection that works seamlessly with your existing systems.
            </p>
          </div>
          
          <div className="trustmark-capabilities-grid">
            <div className="trustmark-capability-card">
              <div className="trustmark-capability-icon">
                <Shield strokeWidth={1.5} />
              </div>
              <h3 className="trustmark-capability-title">Real-time Protection</h3>
              <p className="trustmark-capability-description">
                Continuous monitoring and instant threat blocking, keeping your systems secure around the clock.
              </p>
            </div>
            
            <div className="trustmark-capability-card">
              <div className="trustmark-capability-icon">
                <Zap strokeWidth={1.5} />
              </div>
              <h3 className="trustmark-capability-title">Seamless Integration</h3>
              <p className="trustmark-capability-description">
                Works with all major platforms and CMS solutions. No complex setup required.
              </p>
            </div>
            
            <div className="trustmark-capability-card">
              <div className="trustmark-capability-icon">
                <Lock strokeWidth={1.5} />
              </div>
              <h3 className="trustmark-capability-title">Privacy Focused</h3>
              <p className="trustmark-capability-description">
                Enhanced security without compromising user experience or data privacy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section className="trustmark-demo">
        <div className="trustmark-section-wrapper">
          <div className="trustmark-section-header">
            <h2 className="trustmark-section-title">Experience the difference.</h2>
            <p className="trustmark-section-subtitle">
              See how TrustMark transforms your security experience.
            </p>
          </div>
          
          <div className="trustmark-demo-content">
            <div className="trustmark-demo-image">
              <img src={demoGif} alt="TrustMark Security Demo" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="trustmark-cta">
        <div className="trustmark-section-wrapper">
          <h2 className="trustmark-cta-title">Get Started with TrustMark</h2>
          <p className="trustmark-cta-subtitle">Choose a plan that best suits your security needs.</p>
          <button className="trustmark-btn trustmark-btn-primary" onClick={goToPricing}>
            View Pricing Plans
          </button>
        </div>
      </section>
    </div>
  );
};

export default Trustmark;